import AdsClickOutlined from '@mui/icons-material/AdsClickOutlined';
import ConnectedTvOutlined from '@mui/icons-material/ConnectedTvOutlined';
import CookieOutlined from '@mui/icons-material/CookieOutlined';
import DesktopWindowsOutlined from '@mui/icons-material/DesktopWindowsOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import LanOutlined from '@mui/icons-material/LanOutlined';
import PlayCircleOutlined from '@mui/icons-material/PlayCircleOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import TouchAppOutlined from '@mui/icons-material/TouchAppOutlined';
import TrackChanges from '@mui/icons-material/TrackChanges';
import { OpenXAgentHandCoinsIcon } from '../../../icons/OpenXAgentHandCoinsIcon.tsx';
import type { OpenXAgentMatchedSegment, OpenXAgentMatchedSegmentPanelData } from './types.ts';

type DataPartner = Pick<OpenXAgentMatchedSegment, 'dataPartner' | 'price'>;

const CAPTIFY: DataPartner = { dataPartner: 'Captify', price: { percentOfMedia: '2%', fixedCpm: '$0.50' } };

const DATA_PARTNERS: DataPartner[] = [
  CAPTIFY,
  { dataPartner: 'LiveRamp', price: { fixedCpm: '$0.35' } },
  { dataPartner: 'Eyeota', price: { percentOfMedia: '1.5%' } },
  { dataPartner: 'Lotame', price: { percentOfMedia: '1.75%', fixedCpm: '$0.40' } },
  { dataPartner: 'Experian', price: { fixedCpm: '$0.65' } },
];

const AUDIENCES = [
  { audience: 'Luxury Travel Enthusiasts', category: 'Interest › Travel' },
  { audience: 'Frequent International Shoppers', category: 'Purchase Intent' },
  { audience: 'High Net Worth Individuals', category: 'Demographic › Income' },
  { audience: 'Hybrid Car Intenders', category: 'In-Market › Auto' },
  { audience: 'SUV Shoppers', category: 'In-Market › Auto' },
  { audience: 'Sports Fans', category: 'Interest › Sports' },
  { audience: 'Home Gardeners', category: 'Interest › Home & Garden' },
  { audience: 'Beach Vacationers', category: 'Interest › Travel' },
  { audience: 'Fitness Enthusiasts', category: 'Interest › Health & Fitness' },
  { audience: 'New Parents', category: 'Life Event' },
  { audience: 'Pet Owners', category: 'Interest › Pets' },
  { audience: 'Streaming Subscribers', category: 'Interest › Entertainment' },
  { audience: 'Gamers', category: 'Interest › Gaming' },
  { audience: 'Small Business Owners', category: 'B2B' },
  { audience: 'Tech Early Adopters', category: 'Interest › Technology' },
  { audience: 'Foodies', category: 'Interest › Food & Drink' },
  { audience: 'Home Movers', category: 'Life Event' },
  { audience: 'Fashion Shoppers', category: 'Purchase Intent' },
  { audience: 'Outdoor Adventurers', category: 'Interest › Outdoors' },
  { audience: 'Personal Finance Planners', category: 'Interest › Finance' },
];

const QUALIFIERS = [
  'Ages 25-34',
  'Ages 35-54',
  'High Intent 30d',
  'Desktop & Mobile Web',
  'CTV Viewers',
  '1st Party 180d',
  'Lookalike',
  'Frequent Flyability - Frequent International Shoppers (Ages 35-54, Desktop & Mobile Web)',
];

const DESCRIPTIONS = [
  'Users showing repeated engagement with related content over the last 30 days.',
  'Modelled from verified purchase and browsing signals.',
  'Deterministic audience built from first-party login data.',
  'Probabilistic lookalike expansion of a high-performing seed audience.',
  'Household-level audience reachable across CTV, mobile and desktop.',
];

const formatCount = (thousands: number) =>
  thousands >= 1000 ? `${(thousands / 1000).toFixed(1)}M` : `${Math.round(thousands)}K`;

const MOCK_SEGMENTS: OpenXAgentMatchedSegment[] = QUALIFIERS.flatMap((qualifier, qualifierIndex) =>
  AUDIENCES.map(({ audience, category }, audienceIndex) => {
    const index = qualifierIndex * AUDIENCES.length + audienceIndex;
    const uniqueUsers = 50 + ((index * 3571) % 4950);
    const name = index % 3 === 0 ? `${audience} ${qualifier}`.replaceAll(' ', '_') : `${audience} - ${qualifier}`;

    return {
      id: String(index + 1),
      name,
      description: DESCRIPTIONS[index % DESCRIPTIONS.length] ?? '',
      category,
      ...(DATA_PARTNERS[(audienceIndex + qualifierIndex) % DATA_PARTNERS.length] ?? CAPTIFY),
      uniqueUsers: formatCount(uniqueUsers),
      ctvDevices: formatCount(uniqueUsers * (0.1 + (index % 5) * 0.02)),
      activeIps: formatCount(uniqueUsers * (0.4 + (index % 4) * 0.05)),
      mobileDevices: formatCount(uniqueUsers * (0.55 + (index % 3) * 0.1)),
      isFree: index % 4 === 1,
    };
  })
);

export const MOCK_MATCHED_SEGMENT_PANEL: OpenXAgentMatchedSegmentPanelData = {
  title: 'Matches Details - Highest Scale',
  filters: [
    { id: 'region', label: 'Region', value: 'United States' },
    { id: 'segmentsSearchMode', label: 'Segments Search Mode', value: 'OpenXSelect Data Marketplace' },
    { id: 'dataPartner', label: 'Data Partner', value: 'Captify' },
  ],
  interestTags: ['Sport', 'Travel & Vacations', 'Gardening'],
  stats: [
    { id: 'averageCpm', icon: OpenXAgentHandCoinsIcon, label: 'Average CPM', value: '$4.82M' },
    { id: 'uniqueUsers', icon: GroupOutlined, label: 'Unique Users', value: '13,8M' },
    { id: 'relevance', icon: TrackChanges, label: 'Relevance', value: '92%' },
  ],
  metricGroups: [
    {
      id: 'engagement',
      title: 'Engagement',
      metrics: [
        { id: 'ctr', icon: AdsClickOutlined, label: 'CTR', value: '0.38%' },
        { id: 'videoCompletion', icon: PlayCircleOutlined, label: 'Video Completion', value: '0.38%' },
      ],
    },
    {
      id: 'scale',
      title: 'Scale',
      metrics: [
        { id: 'totalRequests', icon: TouchAppOutlined, label: 'Total Requests', value: '18.6B' },
        { id: 'activeIps', icon: LanOutlined, label: 'Active IPs', value: '6.1M' },
        { id: 'cookies', icon: CookieOutlined, label: 'Cookies', value: '9.8M' },
      ],
    },
    {
      id: 'deviceDistribution',
      title: 'Device Distribution',
      metrics: [
        { id: 'mobile', icon: SmartphoneOutlined, label: 'Mobile', value: '54%' },
        { id: 'ctv', icon: ConnectedTvOutlined, label: 'CTV', value: '28%' },
        { id: 'desktop', icon: DesktopWindowsOutlined, label: 'Desktop', value: '18%' },
      ],
    },
  ],
  pricing: {
    partners: [
      { id: 'captify', name: 'Captify', percentOfMedia: '2%', fixedCpm: '$0.50' },
      { id: 'liveRamp', name: 'LiveRamp', percentOfMedia: '1.5%', fixedCpm: '$0.35' },
      { id: 'eyeota', name: 'Eyeota', percentOfMedia: '1.5%', fixedCpm: '$0.35' },
      { id: 'lotame', name: 'Lotame', percentOfMedia: '1.75%', fixedCpm: '$0.40' },
      { id: 'experian', name: 'Experian', percentOfMedia: '1%', fixedCpm: '$0.65' },
    ],
    averagePercentOfMedia: '2.17%',
    averageFixedCpm: '$0.53',
  },
  items: MOCK_SEGMENTS,
};
